import { ImageCaptionDTO, RawImageCaption } from './api.types';
import { HarborBookingType } from '@/constant';

export type Club = {
  id: string;
  name: string;
  abbreviation: string;
  description: string;
  logo: string;
  media: string[];
  location: string;
  website: string;
  contact_email: string;
  contact_person_name: string;
  contact_person_phone: string;
  contact_person_phone_country_short_code?: string;
  street_address: string;
  postal_code: string;
  city: string;
  business_id: string;
  spv_id: string;
  status: number;
  region_id: string;
  region: {
    id: string;
    name: string;
  };
  membershipStatus: number;
  harbors: {
    id: string;
    name: string;
    location: string;
  }[];
  yachtClubAdmin: {
    id: string;
    name: string;
    email: string;
    profile_picture: string;
  };
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
};

// Facility with condition information
export interface FacilityWithCondition {
  facility: string;
  condition: string;
  last_updated?: string;
  updated_by?: {
    id: string;
    name: string;
  };
  availability_status?: 'always' | 'seasonal' | 'closed';
  seasonal_availability?: {
    spring?: SeasonDateRange[];
    summer?: SeasonDateRange[];
    autumn?: SeasonDateRange[];
    winter?: SeasonDateRange[];
  };
}

export interface IHarborData {
  updated_at?: string;
  // eslint-disable-next-line
  manager?: any;
  id?: string;
  name?: string;
  description?: string;
  location?: string;
  city?: string;
  latitude?: number;
  vhf_channel?: number;
  longitude?: number;
  contact_email?: string;
  contact_phone?: string;
  country_code?: string;
  country_short_code?: string;
  contact_phone_country_short_code?: string;
  product_vat_config_id?: string | null;
  // website?: string;
  default_check_in_time?: string;
  default_check_out_time?: string;
  check_in_window_start?: string;
  check_in_window_end?: string;
  check_out_window_start?: string;
  check_out_window_end?: string;
  timezone?: string;
  berth_capacity?: number;
  admin_email?: string;
  min_depth?: number;
  max_depth?: number;
  depth_unit?: 'm' | 'ft';
  facilities?: string[];
  facilities_with_conditions?: FacilityWithCondition[];
  facility_conditions?: Array<{
    facility_name: string;
    condition: string;
    updated_at?: string;
    updated_by?: { id: string; name?: string; email?: string };
  }>;
  amenities?: string[];
  business_hours?: string;
  status?: number;
  logo?: File | string | null;
  fairway_map?: (File | string)[] | null;
  fairway_entrance_map?: (File | string)[] | null;
  harbor_type?: string;
  media?: (File | string)[];
  media_captions?: ImageCaptionDTO[];
  image_captions?: RawImageCaption[];
  fairway_map_captions?: ImageCaptionDTO[];
  fairway_entrance_map_captions?: ImageCaptionDTO[];
  berth_image?: File | string | null;
  berth_image_captions?: ImageCaptionDTO[];
  created_at?: string;
  external_url?: string;
  website_url?: string;
  featured?: boolean;
  marina?: {
    id?: string; // Optional to match BoatModal.tsx
    name: string;
  };
  marina_id?: string;
  category?: string;
  regionIds?: string[];
  regions?: Array<{
    id: string;
    name: string;
    status: number;
  }>;
  yachtClub?: { id: string; name: string };
  yachtClubId?: string;
  clubId?: string;
  club?: Club;
  send_booking_to_admin?: boolean;
  mediation_response_deadline_hours?: number | null;
  booking_enabled?: boolean;
  booking_type?: HarborBookingType;
  businessHours?: string;
  whatsapp_link?: string;
  facebook_link?: string;
  instagram_link?: string;
  pdf_link?: string;
  terms_pdf?: File | string | null;
  legal_acceptance_summary?:
    | import('./legal-compliance.types').LegalAcceptanceSummary
    | null;
  admin_notes?: string;
  primary_language_spoken?: string;
  mooring_style?: string;
  coastal_area?: string;
  yachtClubIds?: string[];
  serviceIds?: string[];
  services?: Array<{
    id: string;
    name: string;
    status: number;
    service_provider?: {
      id: string;
      status: number;
    };
  }>;
  deletion_status?: HarborDeletionStatus;
  deletion_requested_at?: string;
  harbor_status?: number;
  seasonality_support?: boolean;
  paytrail_merchant_id?: string;
  seasonal_dates?: {
    spring?: SeasonDateRange[];
    summer?: SeasonDateRange[];
    autumn?: SeasonDateRange[];
    winter?: SeasonDateRange[];
  };
  /** Translations keyed by ISO language code (e.g. "en", "fi", "de", "sv").
   *  Only the description field is currently translated by the backend. */
  translations?: Record<string, { description?: string }>;
}

export interface SeasonDateRange {
  start_date?: string;
  end_date?: string;
  is_closed?: boolean;
}

// Non-paginated list response for Harbor Admin's "my harbors" endpoint
export type MyHarborListResponse = HarborResponse[];

export interface HarborResponse {
  id: string;
  name: string;
  description: string;
  location: string;
  latitude: number;
  longitude: number;
  vhf_channel?: number;
  contact_email: string;
  contact_phone: string;
  country_code: string;
  category: string;
  country_short_code: string;
  contact_phone_country_short_code?: string;
  product_vat_config_id?: string | null;
  website: string;
  berth_capacity: number;
  website_url?: string;
  admin_email: string;
  min_depth?: number;
  max_depth?: number;
  depth_unit?: 'm' | 'ft';
  facilities: string[];
  facilities_with_conditions?: FacilityWithCondition[];
  amenities: string[];
  business_hours: string;
  default_check_in_time?: string;
  default_check_out_time?: string;
  check_in_window_start?: string;
  check_in_window_end?: string;
  check_out_window_start?: string;
  check_out_window_end?: string;
  timezone?: string;
  status: number;
  created_at: string;
  logo?: string;
  fairway_map?: string[];
  fairway_entrance_map?: string[];
  media?: string[];
  media_captions?: ImageCaptionDTO[];
  image_captions?: RawImageCaption[];
  fairway_map_captions?: ImageCaptionDTO[];
  fairway_entrance_map_captions?: ImageCaptionDTO[];
  berth_image?: string | null;
  berth_image_captions?: ImageCaptionDTO[];
  external_url?: string;
  featured?: boolean;
  manager: { id?: string; name?: string; email?: string };
  marina_id?: string;
  regionIds?: string[];
  regions?: string[];
  harbor_status?: number;
  booking_enabled?: boolean;
  booking_type?: HarborBookingType;
  terms_pdf?: string | null;
}

export interface HarborListQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  is_featured?: boolean;
}

export interface BulkImportHarborRowPayload {
  name: string;
  description: string;
  location: string;
  latitude: number | string;
  longitude: number | string;
  category: string;
  coastal_area: string;
  country_short_code: string;
  min_depth?: number;
  max_depth?: number;
  berth_capacity?: number;
}

export interface BulkImportHarborsPayload {
  rows: BulkImportHarborRowPayload[];
}

export interface BulkImportFieldError {
  row: number;
  field: string;
  message: string;
}

export interface BulkImportCreatedHarbor {
  id: string;
  name: string;
  location: string;
}

export interface BulkImportHarborsResult {
  created: BulkImportCreatedHarbor[];
}

export interface BulkUpdateHarborsPayload {
  file: File;
  dryRun: boolean;
}

export interface BulkUpdateValidationError {
  row: number;
  field: string;
  message: string;
  harborId?: string;
  name?: string;
}

export interface BulkUpdateChangedField {
  field: string;
  oldValue: string;
  newValue: string;
}

export interface BulkUpdateUpdatedHarbor {
  harborId: string;
  name: string;
  changedFields: BulkUpdateChangedField[];
}

export interface BulkUpdateHarborsResult {
  updated: BulkUpdateUpdatedHarbor[];
  unchanged: BulkUpdateUpdatedHarbor[];
  ignoredColumns: string[];
}

export interface FeaturedHarborResponse {
  id: string;
  name: string;
  description: string;
  location: string;
  latitude: number;
  longitude: number;
  berth_capacity: number;
  depth: number;
  contact_phone: string;
  contact_email: string;
  facilities: string[];
  amenities: string[];
  business_hours: Record<string, unknown>;
  website_url: string;
  logo: string;
  media: string[];
  status: string;
  featured: boolean;
  created_at: string;
  updated_at: string;
  manager: Record<string, unknown>;
}

export interface UpdateFeaturedPayload {
  harborId: string;
  featured: boolean;
}

export interface MediaPayload {
  id?: string;
  media: File[];
}

export interface CoastalAreaResponse {
  id: string | number;
  name: string;
  description: string;
  label: string;
}

export enum HarborStatusType {
  INACTIVE = 0,
  ACTIVE = 1,
  DELETED = 2,
  PENDING_DELETION = 3,
  UNDER_RENOVATION = 4,
}

export enum HarborDeletionStatus {
  ACTIVE = 'ACTIVE',
  PENDING_DELETION = 'PENDING_DELETION',
  DELETED = 'DELETED',
}

export interface HarborDeletionRequest {
  id: string;
  name: string;
  description?: string;
  location: string;
  contact_email: string;
  country_code: string;
  contact_phone: string;
  status: number;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  deletion_requested_by: {
    id: string;
    name: string;
    email: string;
    user_type: number;
    profile_picture?: string;
  };
  regions: Array<{
    id: string;
    name: string;
    status: number;
  }>;
  deletion_reason?: string;
  deletion_requested_at: string;
  deletion_reviewed_at?: string;
  deletion_reviewed_by?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface HarborDeletionRequestPayload {
  reason: string;
}

export interface HarborDeletionListQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: number | string;
}

export interface PaginatedHarborDeletionResponse {
  data: HarborDeletionRequest[];
  pageLimit: number;
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export interface HarborTranslationPayload {
  name?: string;
  description?: string;
  admin_notes?: string;
  source_language?: string;
}

export interface HarborTranslations {
  translations: Record<
    string,
    Partial<{
      name: string;
      description: string;
      admin_notes: string;
    }>
  >;
  message?: string;
  warning_message?: string;
}

export interface HarborAccessInformation {
  access_code: string | null;
  access_code_language: string | null;
  access_code_usage_description_translations: Record<string, string> | null;
}

export interface UpdateHarborAccessInformationPayload {
  access_code?: string | null;
  access_code_usage_description?: string | null;
  access_code_language?: string;
  translations?: Record<string, string>;
}

export interface TranslateHarborAccessInformationPayload {
  access_code_usage_description: string;
  source_language: string;
}

export interface HarborAccessInformationTranslations {
  translations: Record<string, string>;
  message?: string;
  warning_message?: string;
}

export interface HarborKYCData {
  id: string;
  entity_type: 'harbor';
  entity_id: string;
  legal_name: string;
  business_id: string;
  vat_id: string;
  address_line_1: string;
  address_line_2: string;
  postal_code: string;
  city: string;
  state: string;
  country: string;
  account_holder_name: string;
  account_number: string;
  iban: string;
  bic: string;
  bank_name: string;
  kyc_status: 'pending' | 'approved' | 'rejected';
  admin_notes: string;
  reviewed_by: string;
  reviewed_at: string;
  created_at: string;
  updated_at: string;
}
